// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { getUserProperty } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { createErrorAnalytics } from '@atlassian/jira-polaris-lib-errors/src/controllers/index.tsx';
import { UnknownError } from '@atlassian/jira-polaris-lib-errors/src/common/utils/unknown-error/index.tsx';
import { logSafeErrorWithoutCustomerDataWrapper } from '@atlassian/jira-polaris-lib-errors/src/common/utils/index.tsx';
import {
	QUICKSTART_UI_STATUS_NOT_LOADED,
	QUICKSTART_UI_STATUS_OPEN,
	QUICKSTART_UI_STATUS_MINIMIZED,
	QUICKSTART_UI_STATUS_DISMISSED,
	QUICKSTART_PANEL_VISIBILITY_STATE_KEY,
	type QuickstartUiStatus,
} from '../../constants.tsx';
import type { State } from '../../model/types.tsx';

type Props = {
	accountId: AccountId | null;
};

export const stringToQuickstartUiStatus = (statusString: string): QuickstartUiStatus => {
	switch (statusString) {
		case 'NOT_LOADED':
			return QUICKSTART_UI_STATUS_NOT_LOADED;
		case 'OPEN':
			return QUICKSTART_UI_STATUS_OPEN;
		case 'MINIMIZED':
			return QUICKSTART_UI_STATUS_MINIMIZED;
		case 'DISMISSED':
			return QUICKSTART_UI_STATUS_DISMISSED;
		default:
			return QUICKSTART_UI_STATUS_OPEN; // fallback to OPEN
	}
};

export const loadQuickstartBannerVisibility =
	({ accountId }: Props): Action<State, Props> =>
	async ({ getState, setState }) => {
		const { visibility: stateVisibility } = getState();

		if (!accountId || stateVisibility !== QUICKSTART_UI_STATUS_NOT_LOADED) {
			return;
		}

		try {
			const visibilityString = await getUserProperty<string>(
				accountId,
				QUICKSTART_PANEL_VISIBILITY_STATE_KEY,
			);

			const visibility = stringToQuickstartUiStatus(visibilityString);

			setState({ visibility });
		} catch (error) {
			if (error instanceof FetchError && error.statusCode === 404) {
				setState({ visibility: QUICKSTART_UI_STATUS_OPEN }); // user property is not set yet
			} else {
				const unknownErrorText = `Failed to load quickstart banner visibility with account id: ${accountId}`;

				fireErrorAnalytics(
					createErrorAnalytics(
						'polaris.error.loadQuickstartBannerVisibilityFailed',
						error instanceof Error ? error : new UnknownError(unknownErrorText, error),
					),
				);

				logSafeErrorWithoutCustomerDataWrapper(
					'polaris.error.controllers.quickstart-banner.loadQuickstartBannerVisibility',
					'Failed to load quickstart banner visibility',
					error instanceof Error ? error : new UnknownError(unknownErrorText, error),
				);
			}
		}
	};
